import {
  fetchOncologySubmissionData,
  OncologySubmissionApiResponse,
} from "../../../api/bigQuery/oncologySubmissionOrders";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { logError } from "../../../utils/customLogger";
import { OncologySubmissionFilterPayload } from "../hooks/useFilterPayload";
import { OncologySubmissionTableState } from "../hooks/useOncologySubmissionTableData";

/**
 * makePostCallForNextPagesDataFetch - API Call for Pagination
 *
 * Purpose: Fetches next page of data when user scrolls to the end.
 * Appends new data to existing tableData without resetting.
 *
 * @param payload - Filter payload containing search params with incremented page number
 * @param setOncologySubmissionState - State setter for API response
 * @param setTableData - State setter for table data (appends to existing data)
 * @param isMountedRef - Ref to check if component is still mounted
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: OncologySubmissionFilterPayload,
  setOncologySubmissionState: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  isMountedRef?: React.MutableRefObject<boolean>,
): Promise<void> => {
  try {
    if (isMountedRef && !isMountedRef.current) return;

    setOncologySubmissionState((prev) => ({
      ...prev,
      showInlineLoader: true,
      error: null,
    }));

    const response: OncologySubmissionApiResponse =
      await fetchOncologySubmissionData(payload);

    if (isMountedRef && !isMountedRef.current) return;

    if (response.success) {
      setOncologySubmissionState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });

      // Append new data to existing data
      setTableData((prevData) => [...prevData, ...response.rows]);
    } else {
      setOncologySubmissionState((prev) => ({
        ...prev,
        showInlineLoader: false,
        error: response.message || "Failed to fetch next page",
      }));
    }
  } catch (error) {
    if (isMountedRef && !isMountedRef.current) return;

    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setOncologySubmissionState((prev) => ({
      ...prev,
      showInlineLoader: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForNextPagesDataFetch");
  }
};
